import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { apiService } from "../services/api";
import { Article, Event } from "../types";
import { CompactEventCalendar, FeedItem } from "../components";

type FeedItemType = {
  type: "article" | "event";
  data: Article | Event;
  date: Date;
};

export const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const [feedItems, setFeedItems] = useState<FeedItemType[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [events, setEvents] = useState<Event[]>([]);
  const articlesPageRef = useRef(0);
  const eventsPageRef = useRef(0);
  const loadingRef = useRef(false);

  const loadMoreContent = useCallback(async () => {
    if (loadingRef.current || !hasMore) return;

    try {
      loadingRef.current = true;
      setLoading(true);
      const [articlesResponse, eventsResponse] = await Promise.all([
        apiService.getArticles(articlesPageRef.current * 10, 10, true),
        apiService.getEvents(eventsPageRef.current * 10, 10, true),
      ]);

      const newItems: FeedItemType[] = [
        ...articlesResponse.articles.map((article) => ({
          type: "article" as const,
          data: article,
          date: new Date(article.published_at || article.created_at),
        })),
        ...eventsResponse.events.map((event) => ({
          type: "event" as const,
          data: event,
          date: new Date(event.event_date),
        })),
      ];

      // Sort by date (newest first)
      newItems.sort((a, b) => b.date.getTime() - a.date.getTime());

      setFeedItems((prev) => {
        const combined = [...prev, ...newItems];
        // Remove duplicates based on id and type
        const unique = combined.filter(
          (item, index, self) =>
            index ===
            self.findIndex(
              (t) => t.data.id === item.data.id && t.type === item.type,
            ),
        );
        return unique.sort((a, b) => b.date.getTime() - a.date.getTime());
      });

      articlesPageRef.current += 1;
      eventsPageRef.current += 1;
      setHasMore(
        articlesResponse.articles.length === 10 ||
          eventsResponse.events.length === 10,
      );
    } catch (error) {
      console.error("Error fetching content:", error);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [hasMore]);

  useEffect(() => {
    loadMoreContent();
  }, [loadMoreContent]);

  // Fetch events for calendar
  useEffect(() => {
    const fetchEventsForCalendar = async () => {
      try {
        const response = await apiService.getEvents(0, 100, true);
        setEvents(response.events);
      } catch (error) {
        console.error("Error fetching events for calendar:", error);
      }
    };
    fetchEventsForCalendar();
  }, []);

  // Scroll handler for feed container (not window)
  const feedContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const feedContainer = feedContainerRef.current;
    if (!feedContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = feedContainer;
      // Load more when near bottom (within 500px)
      if (
        scrollHeight - scrollTop - clientHeight < 500 &&
        !loading &&
        hasMore
      ) {
        loadMoreContent();
      }
    };

    feedContainer.addEventListener("scroll", handleScroll);
    return () => feedContainer.removeEventListener("scroll", handleScroll);
  }, [loading, hasMore, loadMoreContent]);

  return (
    <div className="h-full flex flex-col mx-0 lg:mx-28 font-sans">
      <div className="flex-1 flex flex-col lg:flex-row h-full items-stretch lg:items-start min-h-0">
        {/* Information Section — mobile only, above feed */}
        <div className="lg:hidden px-4 pt-2 pb-3 shrink-0">
          <div className="bg-white/95 backdrop-blur-sm rounded-xl p-3 shadow-xl border border-white/20 space-y-3">
            <CompactEventCalendar events={events} />
            <div className="space-y-1">
              <h3 className="text-church-brown text-sm font-semibold mb-1.5 font-sans">
                {t("home.schedule")}
              </h3>
              <p className="text-gray-800 text-sm leading-snug font-sans">
                {t("schedule.saturday")}
              </p>
              <p className="text-gray-800 text-sm leading-snug font-sans">
                {t("schedule.daily")}
              </p>
            </div>
            <div className="flex justify-between gap-4">
              <div>
                <h3 className="text-church-brown text-sm font-semibold mb-1.5 font-sans">
                  {t("home.location")}
                </h3>
                <div className="text-gray-800 leading-snug space-y-0.5 font-sans">
                  <p className="font-semibold text-gray-900 text-sm">
                    Minihi Levenez
                  </p>
                  <p className="text-gray-700 text-sm">4 rue du Minihi</p>
                  <p className="text-gray-700 text-sm">29800 Treflevenez</p>
                  <p className="text-gray-600 text-sm">Bretagne, France</p>
                </div>
              </div>
              <div className="min-w-0">
                <h3 className="text-church-brown text-sm font-semibold mb-1.5 font-sans">
                  {t("home.contact")}
                </h3>
                <div className="text-gray-800 leading-snug space-y-0.5 font-sans">
                  <p className="text-gray-700 text-sm">{t("schedule.phone")}</p>
                  <p className="text-gray-700 text-sm break-all">
                    Mail :{" "}
                    <a
                      href="mailto:revue.minihi@gmail.com"
                      className="hover:text-church-gold transition-colors"
                    >
                      revue.minihi@gmail.com
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Feed Section - 2/3 */}
        <div
          className="flex-1 lg:w-2/3 h-full min-h-0 overflow-y-auto custom-scrollbar feed-fade [direction:rtl]"
          ref={feedContainerRef}
        >
          <div className="max-w-4xl mx-auto px-6 lg:px-8 lg:pt-2 [direction:ltr]">
            <div className="space-y-6">
              {feedItems.length === 0 && !loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-church-brown mx-auto mb-4"></div>
                  <p className="text-gray-600 text-sm font-sans">
                    {t("common.loading")}
                  </p>
                </div>
              ) : (
                feedItems.map((item) => (
                  <FeedItem key={`${item.type}-${item.data.id}`} item={item} />
                ))
              )}

              {/* Loading indicator */}
              {loading && (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-church-brown mx-auto mb-4"></div>
                  <p className="text-gray-600 text-sm font-sans">
                    {t("common.loading")}
                  </p>
                </div>
              )}

              {/* Load more button */}
              {!loading && hasMore && feedItems.length > 0 && (
                <div className="text-center py-6">
                  <button
                    onClick={loadMoreContent}
                    className="bg-church-brown text-white px-8 py-3 rounded-lg hover:bg-church-dark transition-all duration-200 text-sm font-medium shadow-md hover:shadow-lg font-sans"
                  >
                    {t("common.load_more")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Information Section - 1/3 — desktop sidebar */}
        <div className="hidden lg:block w-1/3 h-full overflow-y-auto custom-scrollbar sticky top-0">
          <div className="p-2">
            <div className="bg-white/95 backdrop-blur-sm rounded-xl p-3 shadow-xl border border-white/20 space-y-3">
              {/* Calendar Component - Top */}
              <div>
                <CompactEventCalendar events={events} />
              </div>

              {/* Schedule Section */}
              <div className="space-y-1">
                <h3 className="text-church-brown text-sm font-semibold mb-1.5 font-sans">
                  {t("home.schedule")}
                </h3>
                <div className="space-y-1">
                  <div className="">
                    <p className="text-gray-800 text-sm leading-snug font-normal font-sans normal-case">
                      {t("schedule.saturday")}
                    </p>
                  </div>
                  <div className="">
                    <p className="text-gray-800 text-sm leading-snug font-normal font-sans normal-case">
                      {t("schedule.daily")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Information - Underneath */}
              <div className="flex justify-between">
                {/* Address Section */}
                <div>
                  <h3 className="text-church-brown text-sm font-semibold mb-1.5 font-sans">
                    {t("home.location")}
                  </h3>
                  <div className="text-gray-800 leading-snug space-y-0.5 font-sans">
                    <p className="font-semibold text-gray-900 text-sm">
                      Minihi Levenez
                    </p>
                    <p className="text-gray-700 text-sm">4 rue du Minihi</p>
                    <p className="text-gray-700 text-sm">29800 Treflevenez</p>
                    <p className="text-gray-600 text-sm">Bretagne, France</p>
                  </div>
                </div>

                {/* Contact Section */}
                <div className="min-w-0">
                  <h3 className="text-church-brown text-sm font-semibold mb-1.5 font-sans">
                    {t("home.contact")}
                  </h3>
                  <div className="text-gray-800 leading-snug space-y-0.5 font-sans">
                    <p className="text-gray-700 text-sm">
                      {t("schedule.phone")}
                    </p>
                    <p className="text-gray-700 text-sm break-all">
                      Mail :{" "}
                      <a
                        href="mailto:revue.minihi@gmail.com"
                        className="hover:text-church-gold transition-colors"
                      >
                        revue.minihi@gmail.com
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
